import AppLogo from '../components/AppLogo';
import DeployProgress from './catalog/DeployProgress';
import FormField from './catalog/FormField';
import { groupFields, isNarrow } from './catalog/FieldGroups';
import MongoTopology from './catalog/MongoTopology';
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { catalogAPI, clustersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Color-coded status pill with a "checking…" neutral state — same pattern as
// AppServicesListPage.js's StatusDot, reused here so catalog releases and raw workloads
// read consistently across the app.
const STATUS_COLOR = {
  running: 'var(--color-success-500)', deployed: 'var(--color-success-500)',
  pending: 'var(--color-warning-500)', failed: 'var(--color-danger-500)',
};
function StatusDot({ status, bootstrapping, stepLabel, step, stepsTotal }) {
  const key = String(status || 'unknown').toLowerCase();
  const color = STATUS_COLOR[key] || 'var(--muted-2)';
  const label = bootstrapping ? `${status} — ${stepLabel} (${step}/${stepsTotal})`
    : `${status}${key === 'pending' ? ' …' : ''}`;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: color + '18',
      color, padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
      {label}
    </span>
  );
}

// Best-effort resource type from chart/topology, used to power the type filter.
const TYPE_KEYS = ['elasticsearch', 'kibana', 'mongo', 'postgres', 'redis'];
function typeOf(r) {
  const hay = `${r.chart || ''} ${r.topology || ''}`.toLowerCase();
  return TYPE_KEYS.find(k => hay.includes(k)) || 'other';
}

// A node pin (manual-PV hostname, or a role-based nodeSelector match) bypasses the
// scheduler's normal candidate filtering the same way a hard taint does — neither offers
// any protection against landing on a dedicated/cordoned node. Warn, don't block: some
// taints are intentional and the operator may want that node anyway (see this session's
// `dedicated: cyber`/`dedicated: cats` nodes, which are real reserved capacity, not a bug).
function taintWarning(nodeName, nodeTaints) {
  const taints = nodeTaints[nodeName];
  if (!taints || !taints.length) return null;
  return `⚠ Tainted: ${taints.map(t => `${t.key}=${t.value || ''}`).join(', ')} — a pod pinned here won't schedule unless it tolerates this.`;
}

// A role label (kubernetes.io/role) can match several nodes — warn if ANY of them is
// tainted, since a nodeSelector by role has no way to express "prefer the untainted one".
function roleTaintWarning(role, nodesByRole, nodeTaints) {
  if (!role) return null;
  const tainted = (nodesByRole[role] || []).filter((n) => nodeTaints[n]);
  if (!tainted.length) return null;
  return `⚠ ${tainted.length} node(s) with role "${role}" are tainted (${tainted.join(', ')}) — a pod may land on one and fail to schedule unless it tolerates that taint.`;
}

// App Catalog — deploy managed open-source app / DB clusters (Redis, MongoDB, PostgreSQL,
// Elasticsearch) via Helm. Deploy is async: returns immediately, page polls for status.
function Catalog() {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [clusterId, setClusterId] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [variantKey, setVariantKey] = useState('');   // chosen topology
  const [releaseName, setReleaseName] = useState('');
  const [namespace, setNamespace] = useState('');
  const [form, setForm] = useState({});
  const [releases, setReleases] = useState([]);
  const [deploying, setDeploying] = useState(false);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nodeRoles, setNodeRoles] = useState([]); // available kubernetes.io/role labels on cluster
  const [clusterNodes, setClusterNodes] = useState([]); // real node names, for the manual-PV node picker
  const [nodeTaints, setNodeTaints] = useState({}); // node name -> [{key,value,effect}] (NoSchedule/NoExecute only)
  const [nodesByRole, setNodesByRole] = useState({}); // role label -> [node names with that role]
  const [storageClasses, setStorageClasses] = useState([]); // this cluster's real StorageClasses, for the storage_class field
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const pollRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [appsRes, clustersRes] = await Promise.all([catalogAPI.apps(), clustersAPI.list()]);
        setApps(appsRes.data);
        setClusters(clustersRes.data);
        if (clustersRes.data.length) setClusterId(String(clustersRes.data[0].id));
      } catch (e) {
        setMessage({ type: 'err', text: 'Failed to load catalog: ' + (e.response?.data?.detail || e.message) });
      } finally { setLoading(false); }
    };
    load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (!clusterId) return;
    refreshReleases();
    // Fetch the cluster's node-role labels so the Node role field can be a validated dropdown.
    // Same response already carries the real node-name list (nodes[].name) for the manual-PV
    // node picker — previously fetched and discarded.
    catalogAPI.nodeRoles(clusterId).then((r) => {
      setNodeRoles(r.data.roles || []);
      setClusterNodes((r.data.nodes || []).map((n) => n.name));
      const taintMap = {};
      (r.data.nodes || []).forEach((n) => { if ((n.taints || []).length) taintMap[n.name] = n.taints; });
      setNodeTaints(taintMap);
      setNodesByRole((r.data.nodes || []).reduce((acc, n) => {
        (n.roles || []).forEach((role) => { (acc[role] = acc[role] || []).push(n.name); });
        return acc;
      }, {}));
    }).catch(() => { setNodeRoles([]); setClusterNodes([]); setNodeTaints({}); setNodesByRole({}); });
    // Real StorageClasses on this cluster, so "Storage class" is a dropdown of what actually
    // exists instead of a free-text field defaulting to "local-path" regardless of cluster —
    // every DB catalog form (Postgres, Mongo, Redis, Elasticsearch) had this gap: a DevOps
    // user deploying to a cluster whose classes are named differently (e.g. jpr-dc-beta-13's
    // local-storage/obd-local-storage/obd42-storageclass) had to already know and type the
    // right name blind, with no feedback if it didn't exist. Matches OpenShift's own pattern
    // (storage class is always an explicit, informed dropdown at creation time, never a
    // silent assumption). 404s on non-Rancher clusters (storage-health is Steve-only) —
    // falls back to an empty list, and the field degrades to free-text (see FormField.js).
    clustersAPI.getStorageHealth(clusterId).then((r) => {
      setStorageClasses(r.data.storage_classes || []);
    }).catch(() => { setStorageClasses([]); });
    // Poll releases every 8s so pending → running transitions show without a manual refresh.
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(refreshReleases, 8000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterId]);

  const refreshReleases = async () => {
    if (!clusterId) return;
    try {
      const res = await catalogAPI.releases(clusterId);
      setReleases(res.data.releases || []);
    } catch (e) { /* keep last */ }
  };

  // Seed the form defaults for a given app + topology variant. storage_class defaults to
  // this cluster's own setting (Manage Clusters → Default Storage Class) instead of the
  // generic "local-path" schema literal, when one has been set — same value the backend
  // falls back to if this field is left untouched, so the UI shows what will actually happen.
  const seedForm = (variant) => {
    const defaults = {};
    Object.entries(variant.form).forEach(([k, spec]) => { defaults[k] = spec.default; });
    const cluster = clusters.find((c) => String(c.id) === String(clusterId));
    if ('storage_class' in defaults && cluster?.default_storage_class) {
      defaults.storage_class = cluster.default_storage_class;
    }
    setForm(defaults);
  };

  const pickApp = (app) => {
    setSelectedApp(app);
    setReleaseName(app.key + '-1');
    setNamespace(`sarvkube-catalog-${app.key}-1`);
    const firstVariant = app.variants[0];
    setVariantKey(firstVariant.key);
    seedForm(firstVariant);
    setMessage(null);
  };

  const changeVariant = (vkey) => {
    setVariantKey(vkey);
    const v = selectedApp.variants.find((x) => x.key === vkey);
    if (v) seedForm(v);
  };

  const currentVariant = () => selectedApp?.variants.find((v) => v.key === variantKey);

  // Keep the suggested namespace in sync with the release name (until user edits it).
  const onReleaseChange = (v) => {
    setReleaseName(v);
    setNamespace(`sarvkube-catalog-${v}`);
  };

  const deploy = async () => {
    if (!clusterId || !selectedApp || !releaseName) return;
    setDeploying(true);
    try {
      const res = await catalogAPI.deploy({
        cluster_id: Number(clusterId),
        app: selectedApp.key,
        variant: variantKey,
        release_name: releaseName,
        namespace: namespace || undefined,
        form,
      });
      // Watch the namespace the API actually used — a script app supplies its own in the
      // form, so the page's `namespace` field may be blank.
      const ns = res?.data?.namespace || form.namespace || namespace;
      setRollout({ name: releaseName, namespace: ns });
      setMessage({ type: 'ok', text: `Deploying ${selectedApp.display_name} "${releaseName}" — progress below.` });
      setSelectedApp(null);
      refreshReleases();
    } catch (e) {
      setMessage({ type: 'err', text: '❌ ' + (e.response?.data?.detail || e.message) });
    } finally { setDeploying(false); }
  };

  const uninstall = async (name) => {
    if (!window.confirm(`Uninstall "${name}"? This deletes its pods and data.`)) return;
    try {
      await catalogAPI.uninstall(name, clusterId);
      setMessage({ type: 'ok', text: `Uninstalled "${name}".` });
      refreshReleases();
    } catch (e) {
      setMessage({ type: 'err', text: '❌ ' + (e.response?.data?.detail || e.message) });
    }
  };

  // The release currently rolling out, so its live progress panel can be shown.
  const [rollout, setRollout] = useState(null);   // {name, namespace}

  // A release only records its chart ("script:redis", "bitnami/postgresql"), so the logo has
  // to be recovered from that string rather than an app key.
  const logoKeyFor = (r) => {
    const hay = `${r.chart || ''} ${r.name || ''}`.toLowerCase();
    for (const k of ['postgres', 'redis', 'mongo', 'elastic', 'rabbit', 'minio', 'kibana']) {
      if (hay.includes(k)) {
        return { postgres: 'postgresql', mongo: 'mongodb', elastic: 'elasticsearch',
                 rabbit: 'rabbitmq' }[k] || k;
      }
    }
    return r.chart || 'app';
  };

  // One field style so inputs, selects and the topology section all match.
  const FIELD = {
    padding: '7px 11px', width: 320, borderRadius: 8, fontSize: 13,
    border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--ink-2)',
  };


  if (loading) return <div style={{ padding: 24 }}>Loading catalog…</div>;

  return (
    <div style={{ padding: 32, maxWidth: 1180, fontFamily: 'Inter, system-ui, sans-serif',
                  color: 'var(--ink-2)' }}>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>App Catalog</h1>
      <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 6, maxWidth: 620, lineHeight: 1.5 }}>
        Production database clusters in one step — replication, sharding, failover and GUI
        included. You choose the topology; SarvKube renders and sequences the manifests.
      </p>

      <div style={{ margin: '16px 0' }}>
        <label style={{ fontWeight: 600, marginRight: 8 }}>Target cluster:</label>
        <select value={clusterId} onChange={(e) => setClusterId(e.target.value)} style={{ ...FIELD, width: 300 }}>
          {clusters.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.connection_type})</option>)}
        </select>
      </div>

      {message && (
        <div style={{ padding: '11px 14px', borderRadius: 10, margin: '14px 0', fontSize: 13,
          background: message.type === 'err' ? 'rgba(220,38,38,0.08)' : 'rgba(16,185,129,0.10)',
          color: 'var(--ink-2)',
          border: '1px solid ' + (message.type === 'err'
            ? 'var(--color-danger-500)' : 'var(--color-success-500)') }}>{message.text}</div>
      )}

      {/* Live rollout. Before this, Deploy said "Starting…" and then nothing for the ~3
          minutes a Patroni cluster takes to come up. */}
      {rollout && (
        <DeployProgress
          clusterId={Number(clusterId)}
          namespace={rollout.namespace}
          releaseName={rollout.name}
          onDone={refreshReleases}
        />
      )}

      <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6,
                   color: 'var(--muted)', marginTop: 26, marginBottom: 12 }}>Available apps</h3>
      {/* A responsive grid rather than a flex wrap, so cards stay aligned in rows. */}
      <div style={{ display: 'grid', gap: 14,
                    gridTemplateColumns: 'repeat(auto-fill, minmax(268px, 1fr))' }}>
        {apps.map((app) => {
          const active = selectedApp?.key === app.key;
          return (
            <div key={app.key} onClick={() => pickApp(app)} style={{
              border: '1px solid ' + (active ? 'var(--brand)' : 'var(--border)'),
              boxShadow: active ? '0 0 0 3px rgba(25,118,210,0.12)' : 'none',
              borderRadius: 12, padding: 16, cursor: 'pointer', background: 'var(--surface)',
              transition: 'border-color .12s, box-shadow .12s' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <AppLogo appKey={app.key} size={44} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{app.display_name}</div>
                  {/* Engine matters to an operator: 'script' is the DBA's own topology,
                      'helm' is an upstream chart. */}
                  <span style={{ display: 'inline-block', marginTop: 4, fontSize: 10,
                                 fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                                 padding: '1px 7px', borderRadius: 20,
                                 background: app.engine === 'script'
                                   ? 'rgba(16,185,129,0.15)' : 'rgba(148,163,184,0.18)',
                                 color: app.engine === 'script'
                                   ? 'var(--color-success-500)' : 'var(--muted)' }}>
                    {app.engine === 'script' ? 'SarvKube topology' : 'Helm chart'}
                  </span>
                </div>
              </div>
              <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 10,
                            lineHeight: 1.5 }}>{app.description}</div>
              <div style={{ color: 'var(--muted-2)', fontSize: 11, marginTop: 8 }}>
                {(app.variants || []).length} topolog{(app.variants || []).length === 1 ? 'y' : 'ies'}
              </div>
            </div>
          );
        })}
      </div>

      {selectedApp && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 22,
                      marginTop: 22, background: 'var(--surface)' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 18 }}>
            <AppLogo appKey={selectedApp.key} size={40} />
            <h3 style={{ margin: 0, fontSize: 17 }}>Deploy {selectedApp.display_name}</h3>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Topology</label>
            <select value={variantKey} onChange={(e) => changeVariant(e.target.value)} style={FIELD}>
              {selectedApp.variants.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Release name</label>
            <input value={releaseName} onChange={(e) => onReleaseChange(e.target.value)} style={FIELD} />
          </div>
          {/* Top-level namespace field only when the variant form doesn't define its own
              'namespace' (script apps supply namespace in their form → avoid the duplicate). */}
          {!(currentVariant()?.form || {}).namespace && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
                Namespace {isSuperAdmin ? '' : '(assigned)'}
              </label>
              <input value={namespace} onChange={(e) => setNamespace(e.target.value)}
                disabled={!isSuperAdmin}
                title={isSuperAdmin ? 'Admins may choose any namespace' : 'Namespace is set by your access'}
                style={{ padding: '6px 10px', width: 300, background: isSuperAdmin ? '#fff' : '#f5f5f5' }} />
            </div>
          )}
          {/* Grouped, not a flat list: PostgreSQL HA has 14 fields and MongoDB ~22, and as
              one unbroken column there was nothing to distinguish a field that reshapes the
              cluster from one that sets a password. */}
          {groupFields(currentVariant()?.form).map((g) => (
            <div key={g.id} style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                            letterSpacing: 0.5, color: 'var(--muted)', marginBottom: 3 }}>
                {g.title}
              </div>
              {g.hint && (
                <div style={{ fontSize: 11.5, color: 'var(--muted-2)', marginBottom: 10,
                              lineHeight: 1.5 }}>{g.hint}</div>
              )}
              {/* Compact fields pair up; text fields get a full row so long values are
                  readable. */}
              <div style={{ display: 'grid', gap: 12,
                            gridTemplateColumns: g.fields.every(([, sp]) => isNarrow(sp))
                              ? 'repeat(auto-fill, minmax(190px, 240px))'
                              : 'repeat(auto-fill, minmax(280px, 320px))' }}>
                {g.fields.map(([key, spec]) => (
                  <FormField key={key} fieldKey={key} spec={spec} form={form}
                             setForm={setForm} nodeRoles={nodeRoles} clusterNodes={clusterNodes}
                             nodeTaints={nodeTaints} nodesByRole={nodesByRole}
                             storageClasses={storageClasses} />
                ))}
              </div>
            </div>
          ))}

          {/* Per-node role placement for multi-node variants (Sentinel etc.): one dropdown
              per node (master + slaves), populated from the cluster's kubernetes.io/role labels.
              node1→w1, node2→w2. Stored as form.roles = [masterRole, slave0Role, ...]. */}
          {currentVariant()?.mongo_topology && (
            <MongoTopology form={form} setForm={setForm} nodeTaints={nodeTaints} />
          )}
          {currentVariant()?.multi_node && (() => {
            const nField = currentVariant().node_field || 'slave_count';
            const slaveN = Number(form[nField] ?? 0);
            const nodeCount = slaveN + 1; // +1 for master
            const roles = form.roles || [];
            const labels = ['Master node', ...Array.from({ length: slaveN }, (_, i) => `Slave ${i + 1} node`)];
            return (
              <div style={{ marginBottom: 12, border: '1px dashed #cbd5e1', borderRadius: 6, padding: 12 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Per-node placement (node role)</label>
                {nodeRoles.length === 0 && (
                  <div style={{ fontSize: 12, color: '#b7791f', marginBottom: 8 }}>
                    ⚠ No <code>kubernetes.io/role</code> labels on this cluster. Leave blank (any node),
                    or label first: <code>kubectl label node &lt;name&gt; kubernetes.io/role=&lt;role&gt;</code>
                  </div>
                )}
                {Array.from({ length: nodeCount }, (_, i) => {
                  const warning = roleTaintWarning(roles[i], nodesByRole, nodeTaints);
                  return (
                    <div key={i} style={{ marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 110, fontSize: 12, color: '#555' }}>{labels[i]}</span>
                        <select value={roles[i] ?? ''} onChange={(e) => {
                          const next = [...roles]; next[i] = e.target.value;
                          setForm({ ...form, roles: next });
                        }} style={{ padding: '5px 10px', width: 200 }}>
                          <option value="">(any node)</option>
                          {nodeRoles.map((r) => (
                            <option key={r} value={r}>{(nodesByRole[r] || []).some((n) => nodeTaints[n]) ? `⚠ ${r}` : r}</option>
                          ))}
                        </select>
                      </div>
                      {warning && (
                        <div style={{ fontSize: 11, color: 'var(--color-warning-500)', marginLeft: 118, marginTop: 2 }}>
                          {warning}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Manual-PV node pickers: one per StatefulSet member, same indexing the backend
              uses in resolve_member_storage (master/slaves/[sentinels or etcd] in that order —
              see pv_member_extra on the variant spec for members with no role-dropdown slot
              of their own, e.g. Sentinel's 3 sentinels or HA's 3 etcd nodes). Only shown when
              Storage mode is set to Manual; required for every member, unlike the role
              picker above (which may be left blank). */}
          {currentVariant()?.multi_node && form.pv_mode === 'manual' && (() => {
            const nField = currentVariant().node_field || 'slave_count';
            const slaveN = Number(form[nField] ?? 0);
            const extra = Number(currentVariant().pv_member_extra || 0);
            const totalMembers = slaveN + 1 + extra;
            const pvNodes = form.pv_nodes || [];
            const baseLabels = ['Master', ...Array.from({ length: slaveN }, (_, i) => `Slave ${i + 1}`)];
            const extraLabel = (i) => (extra === 3 ? `Extra node ${i + 1} (sentinel/etcd)` : `Extra node ${i + 1}`);
            const labels = [...baseLabels, ...Array.from({ length: extra }, (_, i) => extraLabel(i))];
            return (
              <div style={{ marginBottom: 12, border: '1px dashed var(--color-warning-500)', borderRadius: 6, padding: 12 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
                  Storage node (required — Storage mode = Manual)
                </label>
                {clusterNodes.length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--color-warning-500)', marginBottom: 8 }}>
                    ⚠ Could not load this cluster's node list.
                  </div>
                )}
                {Array.from({ length: totalMembers }, (_, i) => {
                  const warning = taintWarning(pvNodes[i], nodeTaints);
                  return (
                    <div key={i} style={{ marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 150, fontSize: 12, color: '#555' }}>{labels[i]}</span>
                        <select value={pvNodes[i] ?? ''} onChange={(e) => {
                          const next = [...pvNodes]; next[i] = e.target.value;
                          setForm({ ...form, pv_nodes: next });
                        }} style={{ padding: '5px 10px', width: 200 }}>
                          <option value="">(select a node)</option>
                          {clusterNodes.map((n) => (
                            <option key={n} value={n}>{nodeTaints[n] ? `⚠ ${n}` : n}</option>
                          ))}
                        </select>
                      </div>
                      {warning && (
                        <div style={{ fontSize: 11, color: 'var(--color-warning-500)', marginLeft: 158, marginTop: 2 }}>
                          {warning}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button onClick={deploy} disabled={deploying} style={{ padding: '9px 20px',
              background: 'var(--brand)', color: '#fff', border: 'none', borderRadius: 8,
              fontWeight: 600, fontSize: 13,
              cursor: deploying ? 'not-allowed' : 'pointer', opacity: deploying ? 0.6 : 1 }}>
              {deploying ? 'Starting…' : 'Deploy'}
            </button>
            <button onClick={() => setSelectedApp(null)} disabled={deploying}
              style={{ padding: '9px 20px', background: 'var(--surface-2)', fontWeight: 600,
                fontSize: 13, color: 'var(--ink-2)', border: '1px solid var(--border)',
                borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      <h3 style={{ marginTop: 28 }}>Deployed releases
        <button onClick={refreshReleases} style={{ fontSize: 12, marginLeft: 8 }}>↻ refresh</button>
        <span style={{ fontSize: 12, color: '#999', marginLeft: 8 }}>(auto-refreshes)</span>
      </h3>

      {releases.length > 0 && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or chart…"
            style={{ ...FIELD, width: 260 }} />
          <div className="filter-tabs">
            {['all', ...TYPE_KEYS, 'other'].map((k) => (
              <button key={k} onClick={() => setTypeFilter(k)}
                className={typeFilter === k ? 'active' : undefined}>
                {k === 'all' ? 'All' : k[0].toUpperCase() + k.slice(1)}
              </button>
            ))}
          </div>
        </div>
      )}

      {releases.length === 0 ? (
        <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--muted-2)',
                      fontSize: 13, border: '1px dashed var(--border)', borderRadius: 12 }}>
          Nothing deployed on this cluster yet — pick an app above.
        </div>
      ) : (() => {
        const q = search.trim().toLowerCase();
        const filtered = releases.filter((r) => {
          if (typeFilter !== 'all' && typeOf(r) !== typeFilter) return false;
          if (q && !(`${r.name} ${r.chart}`.toLowerCase().includes(q))) return false;
          return true;
        });
        if (filtered.length === 0) {
          return (
            <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--muted-2)',
                          fontSize: 13, border: '1px dashed var(--border)', borderRadius: 12 }}>
              No releases match your search/filter.
            </div>
          );
        }
        return (
        <div className="table-card">
          <table className="ds">
            <thead>
              <tr>
                {['Release', 'Chart', 'Status', 'Namespace', ''].map((h) => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const bootstrapping = r.phase === 'bootstrapping' && r.step_label;
                return (
                  <tr key={r.app_service_id} onClick={() => navigate(`/catalog/${r.app_service_id}`)}
                    style={{ cursor: 'pointer' }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <AppLogo appKey={logoKeyFor(r)} size={26} />
                        <span style={{ fontWeight: 600 }}>{r.name}</span>
                      </div>
                    </td>
                    <td style={{ color: 'var(--muted)' }}>{r.chart}</td>
                    <td>
                      <StatusDot status={r.status} bootstrapping={bootstrapping}
                        stepLabel={r.step_label} step={r.step} stepsTotal={r.steps_total} />
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--muted)' }}>{r.namespace}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button onClick={(e) => { e.stopPropagation(); uninstall(r.name); }}
                        className="btn btn-tinted-danger btn-sm">Uninstall</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        );
      })()}
    </div>
  );
}

export default Catalog;
